// The client's proof review page (/proof/:uuid) — Stephanie 2026-10-12.
// Opened from the "Review your proof" button in the rep's email. No login:
// the share link is the key. Shows the client's company, project, item and the
// proof — never the PO #, supplier or costs — with Approve and Request changes.
// The rep, the other rep and production are alerted either way.
// Approving needs a sign-off (Stephanie 2026-10-14, wording Jake 2026-10-19):
// tick "I have reviewed this proof and agree" + the terms, type your full name;
// today's date is shown. Request changes needs none of it.
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/Button";
import { ProofPreview } from "@/components/proofs/ProofPreview";

interface Review {
  status: "awaiting_client" | "client_approved" | "rejected";
  version: number;
  clientName: string;
  projectName: string;
  item: string;
  changesRequested: string | null;
  file: { url: string | null; name: string; mimeType: string } | null;
  terms?: { version: string; note: string; agree: string; points: string[] };
  signature?: { name: string; signedAt: string } | null;
}

const longDate = (d: Date) => d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

export function PublicProofPage() {
  const { uuid = "" } = useParams<{ uuid: string }>();
  const [params] = useSearchParams();
  const contactId = params.get("c") ?? undefined;
  const qc = useQueryClient();
  const key = ["public-proof", uuid];
  const q = useQuery({ queryKey: key, queryFn: () => api<Review>(`/proof/${uuid}/review`, { skipAuth: true, skipAuthRefresh: true }), retry: false });
  const [asking, setAsking] = useState(false);
  const [comments, setComments] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [signedName, setSignedName] = useState("");

  // Records that the client opened it (best effort).
  useEffect(() => {
    void api(`/proof/${uuid}`, { skipAuth: true, skipAuthRefresh: true }).catch(() => undefined);
  }, [uuid]);

  const decide = useMutation({
    mutationFn: (kind: "approve" | "reject") =>
      api(`/proof/${uuid}/${kind}`, {
        method: "POST",
        skipAuth: true,
        skipAuthRefresh: true,
        body: JSON.stringify(
          kind === "approve"
            ? { agreed, signed_name: signedName.trim(), terms_version: q.data?.terms?.version, ...(contactId ? { contact_id: contactId } : {}) }
            : { reason: comments.trim(), ...(contactId ? { contact_id: contactId } : {}) },
        ),
      }),
    onSuccess: () => { setError(null); void qc.invalidateQueries({ queryKey: key }); },
    onError: (err) => {
      const b = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      const code = err instanceof ApiError ? (err.body as { code?: string } | null)?.code : undefined;
      setError(
        code === "signature_required" && b?.error
          ? b.error
          : b?.error
            ? "This proof can't be changed any more — it may already have been approved or updated."
            : "Something went wrong. Please try again.",
      );
    },
  });

  const r = q.data;
  return (
    <div className="min-h-screen bg-canvas text-ink">
      <header className="border-b border-rule bg-white px-6 py-4">
        <Logo className="h-9 w-auto" />
      </header>
      <main className="mx-auto max-w-4xl px-4 py-8 text-[16px]">
        {q.isLoading ? (
          <div className="h-64 animate-pulse rounded-md bg-white" />
        ) : !r ? (
          <p className="rounded-md bg-white p-8 text-center">This proof link isn't available. Please contact your rep at other.</p>
        ) : (
          <>
            <p className="text-sm uppercase tracking-[0.12em] text-muted">{r.clientName}</p>
            <h1 className="mt-1 text-3xl font-semibold tracking-[-0.5px]">Your proof{r.version > 1 ? " (revised)" : ""}</h1>
            <p className="mt-1 text-muted">{r.projectName} · {r.item}</p>
            <div className="mt-6"><ProofPreview file={r.file} /></div>
            <div className="mt-6 rounded-md border border-rule bg-white p-5">
              {r.status === "client_approved" ? (
                <div>
                  <p className="font-semibold">Thank you — this proof is approved. Your rep has been notified.</p>
                  {r.signature && (
                    <p className="mt-2 text-muted">
                      Signed by {r.signature.name} on {longDate(new Date(r.signature.signedAt))}.
                    </p>
                  )}
                </div>
              ) : r.status === "rejected" ? (
                <div>
                  <p className="font-semibold">Thank you — your changes have been sent to your rep.</p>
                  {r.changesRequested && <p className="mt-2 whitespace-pre-line text-muted">"{r.changesRequested}"</p>}
                </div>
              ) : asking ? (
                <div className="space-y-3">
                  <label className="block">
                    <span className="mb-1 block font-semibold">What would you like changed?</span>
                    <textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={5} maxLength={2000} className="w-full rounded-md border border-rule bg-white px-3 py-2" autoFocus />
                  </label>
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button variant="outline" size="lg" onClick={() => setAsking(false)}>Back</Button>
                    <Button size="lg" onClick={() => decide.mutate("reject")} disabled={!comments.trim() || decide.isPending}>{decide.isPending ? "Sending…" : "Send changes"}</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-5">
                  {r.terms && (
                    <>
                      <p className="font-semibold">{r.terms.note}</p>
                      <label className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={agreed}
                          onChange={(e) => setAgreed(e.target.checked)}
                          className="mt-1 h-5 w-5 shrink-0 accent-ink"
                        />
                        <span>
                          <span className="font-semibold">{r.terms.agree}</span>
                          <ul className="mt-2 list-disc space-y-1 pl-5 text-muted">
                            {r.terms.points.map((t) => <li key={t}>{t}</li>)}
                          </ul>
                        </span>
                      </label>
                      <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
                        <label className="block">
                          <span className="mb-1 block font-semibold">Full name (your signature)</span>
                          <input
                            type="text"
                            value={signedName}
                            onChange={(e) => setSignedName(e.target.value)}
                            maxLength={120}
                            autoComplete="name"
                            className="h-11 w-full rounded-md border border-rule bg-white px-3"
                          />
                        </label>
                        <div>
                          <span className="mb-1 block font-semibold">Date</span>
                          <p className="flex h-11 items-center rounded-md border border-rule bg-canvas px-3 text-muted">{longDate(new Date())}</p>
                        </div>
                      </div>
                    </>
                  )}
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <Button variant="outline" size="lg" onClick={() => setAsking(true)}>Request changes</Button>
                    <Button
                      size="lg"
                      onClick={() => decide.mutate("approve")}
                      disabled={decide.isPending || !r.terms || !agreed || signedName.trim().length < 2}
                      title={!agreed || signedName.trim().length < 2 ? "Tick the box and type your full name to approve" : undefined}
                    >
                      {decide.isPending ? "Approving…" : "Approve proof"}
                    </Button>
                  </div>
                </div>
              )}
              {error && <p role="alert" className="mt-3 text-sm text-risk">{error}</p>}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
